import { getToken, getApiBase, clearToken, tryRefresh } from './auth';

/* ================= BASE ================= */

const API_BASE = String(getApiBase()).replace(/\/+$/, '');

const base = () => {
    if (!API_BASE) throw new Error('API base not configured');
    return API_BASE;
};

/* ================= QUERY ================= */

export function buildQuery(params = {}) {
    const qs = new URLSearchParams();

    for (const [k, v] of Object.entries(params)) {
        if (v === undefined || v === null || v === '') continue;

        if (Array.isArray(v)) v.forEach((x) => qs.append(k, String(x)));
        else qs.set(k, String(v));
    }

    const s = qs.toString();
    return s ? `?${s}` : '';
}

/* ================= UNAUTHORIZED ================= */

function notifyUnauthorized() {
    try { clearToken(); } catch {}

    const here = window.location.pathname + window.location.search;
    const loginUrl = '/login';

    if (window.location.pathname !== loginUrl) {
        const url = new URL(loginUrl, window.location.origin);
        url.searchParams.set('from', here);
        window.location.assign(url.toString());
    }
}

/* ================= REQUEST ================= */

async function request(path, { method = 'GET', body, auth = false, headers = {}, signal, _retried = false } = {}) {
    const url = `${base()}${path.startsWith('/') ? path : `/${path}`}`;

    const res = await fetch(url, {
        method,
        headers: {
            ...(body instanceof FormData ? {} : { 'Content-Type': 'application/json' }),
            ...(auth ? { Authorization: `Bearer ${getToken()}` } : {}),
            ...headers,
        },
        body: body ? (body instanceof FormData ? body : JSON.stringify(body)) : undefined,
        credentials: 'include',
        signal,
    });

    if (!res.ok) {
        if (res.status === 401 && auth && !_retried) {
            const refreshed = await tryRefresh();
            if (refreshed) {
                return request(path, { method, body, auth, headers, signal, _retried: true });
            }
            notifyUnauthorized();
            const err = new Error('Unauthorized');
            err.status = 401;
            throw err;
        }

        if (res.status === 401) notifyUnauthorized();

        const raw = await res.text().catch(() => '');
        let payload = null;
        let message = raw || `HTTP ${res.status}`;

        try {
            payload = JSON.parse(raw || '{}');
            message = payload?.message || payload?.error || message;
        } catch {}

        const error = new Error(message);
        error.status = res.status;
        if (payload && typeof payload === 'object') error.payload = payload;
        throw error;
    }

    try { return await res.json(); }
    catch { return {}; }
}

export { request };

/* ================= AUTH ================= */

export const login = (email, password, otp) =>
    request('/auth/login', {
        method: 'POST',
        body: { email, password, ...(otp ? { otp } : {}) },
    });

/* ================= HEALTH ================= */

export const health = () =>
    request('/health');

/* ================= BRANDS ================= */

export const listBrands = () =>
    request('/admin/brands', { auth: true });

const toId = (value) => String(value ?? '');

export const getBrandById = (id) =>
    request(`/admin/brands/${encodeURIComponent(toId(id))}`, { auth: true });

export const createBrand = (data) =>
    request('/admin/brands', {
        method: 'POST',
        body: data,
        auth: true,
    });

export const updateBrand = (id, data) =>
    request(`/admin/brands/${encodeURIComponent(toId(id))}`, {
        method: 'PUT',
        body: data,
        auth: true,
    });

export const deleteBrand = (id) =>
    request(`/admin/brands/${encodeURIComponent(toId(id))}`, {
        method: 'DELETE',
        auth: true,
    });

export const reorderBrands = (ids) =>
    request('/admin/brands/order', {
        method: 'PUT',
        body: { ids },
        auth: true,
    });

/* ================= PRODUCTS ================= */

export const listProducts = (params = {}) =>
    request(`/admin/products${buildQuery(params)}`, { auth: true });

export const getProduct = (id) =>
    request(`/admin/products/${encodeURIComponent(String(id))}`, { auth: true });

export const createProduct = (data) =>
    request('/admin/products', {
        method: 'POST',
        body: data,
        auth: true,
    });

export const updateProduct = (id, data) =>
    request(`/admin/products/${id}`, {
        method: 'PUT',
        body: data,
        auth: true,
    });

export const deleteProduct = (id) =>
    request(`/admin/products/${id}`, {
        method: 'DELETE',
        auth: true,
    });

export const updateProductAvailability = (id, isAvailable) =>
    request(`/admin/products/${id}/availability`, {
        method: 'PATCH',
        body: { isAvailable },
        auth: true,
    });

export const reorderProducts = async (brandId, ids) => {
    const payload = {
        brandId: toId(brandId),
        ids: Array.isArray(ids) ? ids.map((id) => toId(id)).filter(Boolean) : [],
    };

    try {
        return await request('/admin/products/reorder', {
            method: 'POST',
            body: payload,
            auth: true,
        });
    } catch (error) {
        if (error?.status && error.status !== 404 && error.status !== 405) throw error;
        return request('/admin/products/order', {
            method: 'PUT',
            body: payload,
            auth: true,
        });
    }
};

/* ================= MEDIA ================= */

export const listMedia = (params = {}) =>
    request(`/admin/media${buildQuery(params)}`, { auth: true });

export const deleteMedia = (key) =>
    request(`/admin/media${buildQuery({ key })}`, {
        method: 'DELETE',
        auth: true,
    });

/* ================= UPLOAD ================= */

export const signUpload = ({ filename, contentType, folder }) =>
    request('/admin/upload/sign', {
        method: 'POST',
        body: { filename, contentType, folder },
        auth: true,
    });

export const verifyUpload = ({ key }) =>
    request('/admin/upload/verify', {
        method: 'POST',
        body: { key },
        auth: true,
    });

/* ================= HERO CMS ================= */

export const getHero = (page = 'home') =>
    request(`/hero/${page}`);

export const saveHero = (page = 'home', imageKey) =>
    request(`/admin/hero/${page}`, {
        method: 'PUT',
        body: { imageKey },
        auth: true,
    });

/* ================= 2FA MANAGEMENT ================= */

export const list2FAAdmins = () =>
    request('/auth/2fa/admins', { auth: true });

export const setup2FA = (email) =>
    request(`/auth/2fa/setup/${encodeURIComponent(email)}`, {
        method: 'POST',
        auth: true,
    });

export const verify2FA = (email, otp) =>
    request(`/auth/2fa/verify/${encodeURIComponent(email)}`, {
        method: 'POST',
        body: { otp },
        auth: true,
    });

export const disable2FA = (email) =>
    request(`/auth/2fa/disable/${encodeURIComponent(email)}`, {
        method: 'POST',
        auth: true,
    });
